import { useParams } from 'react-router-dom'
import { ArrowLeft, FilePdf, SealCheck, TestTube } from '@phosphor-icons/react'
import { useToast } from '@/ui'
import { buildUrl } from '@/api'
import { errorMessage } from '../../api/core'
import {
  downloadAuthed,
  usePackLot,
  usePackRun,
  useRecallExport,
  type PackLotDetail,
} from '../../api/pack'
import { useLabStatus } from '../../api/quality'
import { useSamples } from '../../api/store'
import { ActionButton } from '../../components/Can'
import { QueryState } from '../../components/Layout'
import { UserName } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  Counter,
  Counters,
  Done,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  type BoardColumn,
} from '../../components/board'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import {
  cartonCount,
  cartonsText,
  kg2,
  labState,
  packLotFigures,
  packLotState,
  pct,
} from '../../model/packing'
import { BoardCard, RecordGrid } from './shared'

/**
 * Pack lot <code> (alignment map V1-F7-04n PackLotRecord; F7.4-i): the lot's record after the
 * close — pouches, counter gap, packing loss, lab — the run record with the V1 maths (packed kilos
 * = pouches × pouch grams, the counter gap is information only, a loss above the line limit is a
 * note, more out than in holds), the family tree back to the farmer lot, and what release waits for.
 */
export function PackLotRecordPage() {
  const { id } = useParams()
  const q = usePackLot(id)
  return (
    <QueryState query={q} label="the pack lot">
      {q.data && <Record pl={q.data} />}
    </QueryState>
  )
}

function Record({ pl }: { pl: PackLotDetail }) {
  const run = usePackRun(pl.runId)
  const lab = useLabStatus(pl.unitId)
  const qcUnit = lab.data?.labUnitId ?? pl.batchUnitId ?? null
  const qc = useSamples({
    unitId: qcUnit ?? '00000000-0000-0000-0000-000000000000',
    kind: 'BATCH_QC',
    page: 1,
    limit: 5,
  })
  const tree = useRecallExport(pl.code)
  const toast = useToast()
  const b = pl.balance ?? run.data?.balance ?? null
  const fig = packLotFigures({
    pouchCount: pl.pouchCount,
    netG: pl.netG,
    labelsPrinted: pl.labelsPrinted,
    inputG: b?.inputG ?? run.data?.inputG ?? null,
    outputG: b?.outputG ?? run.data?.outputG ?? null,
    // The pack lot's own kilos (pouches): the balance's product kilos also count the leftover
    // sack (brand stock), which would hide it.
    productG: pl.packedG ?? b?.productG ?? null,
    lossLimitPct: b?.lossLimitPct ?? null,
  })
  const state = packLotState(pl.status, pl.hold)
  const first = pl.checks.find(
    (c) => c.bizStep === 'POUCH_VERIFIED' && c.payload.position === 'FIRST',
  )
  const seal = qcUnit ? (qc.data?.rows[0]?.sealPayload ?? null) : null
  const labS = labState(lab.data?.overall ?? pl.labOverall)
  const cartons = cartonCount(pl.pouchCount, pl.pouchesPerCarton)
  const openHolds = pl.exceptions.filter((e) => e.status === 'OPEN')
  const lot = tree.data?.lots[0]
  const farmer = lot ? tree.data?.farmers.find((f) => f.farmerId === lot.farmerId) : undefined

  const coder = async () => {
    try {
      await downloadAuthed(buildUrl(`/pack-lots/${pl.id}/coder-sheet.pdf`), `coder-${pl.code}.pdf`)
    } catch (e) {
      toast.error('Coder sheet not downloaded', errorMessage(e))
    }
  }

  const dispatchCols: BoardColumn<PackLotDetail['dispatches'][number]>[] = [
    { id: 'trip', header: 'Trip', cell: (d) => d.shipmentCode },
    { id: 'to', header: 'Customer', cell: (d) => d.toOrg ?? '—' },
    { id: 'inv', header: 'Invoice', cell: (d) => d.challanNo ?? '—' },
    { id: 'pouches', header: 'Pouches', align: 'right', cell: (d) => d.pouches },
    { id: 'when', header: 'Dispatched', hideBelow: 'sm', cell: (d) => boardWhen(d.dispatchedAt) },
  ]

  return (
    <BoardPage
      title={`Pack lot ${pl.code}`}
      actions={
        <>
          <ActionButton variant="secondary" leadingIcon={<FilePdf />} onClick={() => void coder()}>
            Coder sheet
          </ActionButton>
          <LinkButton to="/c/packing/lots" icon={<ArrowLeft aria-hidden="true" />}>
            All pack lots
          </LinkButton>
        </>
      }
    >
      <Pill tone={state.tone}>
        {state.text}
        {run.data?.locationCode ? ` at ${run.data.locationCode}` : ''}
      </Pill>
      <BoardColumns
        main={
          <>
            <Counters>
              <Counter label="Pouches" value={pl.pouchCount ?? '—'} />
              <Counter label="Counter gap" value={fig.counterGap ?? '—'} />
              <Counter label="Packing loss" value={pct(fig.lossPct)} />
              <Counter label="Lab" value={labS.text} loading={lab.isLoading} />
            </Counters>
            <BoardCard title="Run record">
              <RecordGrid
                rows={[
                  {
                    label: 'Started · closed',
                    value: run.data?.startedAt
                      ? `${boardWhen(run.data.startedAt)} to ${run.data.endedAt ? boardWhen(run.data.endedAt).split(' ').pop() : 'open'}`
                      : '—',
                  },
                  {
                    label: 'Pack operator (started the run)',
                    value: <UserName id={pl.operatorId} name={pl.operatorName} />,
                  },
                  {
                    label: 'Cleanout',
                    value: pl.cleanoutById ? (
                      <>
                        hopper photo · by <UserName id={pl.cleanoutById} name={pl.cleanoutByName} />
                      </>
                    ) : (
                      'not recorded'
                    ),
                  },
                  {
                    label: 'First pouch QR',
                    value: first
                      ? first.payload.match === true
                        ? 'scanned, matched'
                        : 'scanned, did not match'
                      : 'not scanned',
                  },
                  {
                    label: 'Sacks scanned in',
                    value: b && b.inputs !== null ? `${b.inputs} · ${kg2(b.inputG)}` : '—',
                  },
                  {
                    label: `Packed (${pl.pouchCount ?? '—'} × ${pl.netG} g)`,
                    value: kg2(fig.packedG),
                    strong: true,
                  },
                  { label: 'Leftover sack', value: fig.leftoverG ? kg2(fig.leftoverG) : '—' },
                  {
                    label: 'Packing loss',
                    value: fig.lossG !== null ? `${kg2(fig.lossG)} · ${pct(fig.lossPct)}` : '—',
                    strong: true,
                  },
                  {
                    label: 'Cartons (+1 taps)',
                    value: cartonsText(pl.pouchCount, pl.pouchesPerCarton),
                  },
                  b?.lossLimitPct !== null &&
                    b?.lossLimitPct !== undefined && {
                      label: `Loss limit, ${run.data?.equipmentCode ?? 'line'}`,
                      value: `${pct(b.lossLimitPct, 1)} · above it: a note`,
                    },
                  { label: 'Codes printed', value: fig.codesPrinted ?? '—' },
                  {
                    label: `Counter gap (${fig.codesPrinted ?? '—'} − ${pl.pouchCount ?? '—'})`,
                    value: fig.counterGap !== null ? `${fig.counterGap} · information` : '—',
                  },
                ]}
              />
              <Note>
                Sack kilos come from each sack’s record. There are no spot weights: packed kilos =
                pouches × {pl.netG} g, so overfill shows as loss. A loss above the limit is a note
                and never holds; more out than in holds the lot. Spoiled codes and test prints are
                not typed, so the counter gap is information only.
              </Note>
              {fig.surplus && (
                <Pill tone="bad" wrap>
                  More out than in: {kg2(-(fig.lossG ?? 0))} surplus. The lot is held.
                </Pill>
              )}
              {fig.aboveLimit && !fig.surplus && (
                <Pill tone="wait">Loss above the line limit · note only</Pill>
              )}
            </BoardCard>

            <BoardCard title="Family tree">
              {tree.isLoading ? (
                <Note>Loading…</Note>
              ) : tree.error ? (
                <Note className="text-danger">{errorMessage(tree.error)}</Note>
              ) : (
                <ol className="flex flex-col gap-1.5 text-[15px]">
                  {(tree.data?.lots ?? []).map((l) => (
                    <li key={l.unitId}>
                      <b>{l.code}</b> · {farmer?.name ?? farmer?.farmerCode ?? 'farmer'} ·{' '}
                      {kg2(l.netG)}
                    </li>
                  ))}
                  {(tree.data?.runs ?? [])
                    .filter((r) => r.stepCode !== 'PACK')
                    .map((r) => (
                      <li key={r.id} className="pl-4">
                        ↳ {r.code} · {r.stepCode.toLowerCase()} · {r.status.toLowerCase()}
                      </li>
                    ))}
                  {(tree.data?.batches ?? [])
                    .filter((x) => x.stream === 'MAIN')
                    .map((x) => (
                      <li key={x.unitId} className="pl-8">
                        ↳ {x.code} · {kg2(x.netG)}
                      </li>
                    ))}
                  <li className="pl-12">
                    ↳ <b>{pl.code}</b>
                  </li>
                </ol>
              )}
              <Note>Written at close. Each link carries the kilos it gave.</Note>
            </BoardCard>

            {pl.dispatches.length > 0 && (
              <BoardCard title="Dispatched">
                <BoardTable
                  caption="Trips"
                  columns={dispatchCols}
                  rows={pl.dispatches}
                  getRowId={(d) => `${d.shipmentId}-${d.pouches}-${d.dispatchedAt}`}
                />
              </BoardCard>
            )}
          </>
        }
        side={
          <SidePanel label="Before release" title="Before release">
            <div className="flex flex-col gap-1.5">
              <Done done={labS.tone === 'ok'}>
                Lab {labS.text.toLowerCase()}
                {seal ? ` · QC seal ${seal}` : ''}
              </Done>
              <Done done={pl.hold === 'NONE' && openHolds.every((e) => e.type !== 'LAB_FAIL')}>
                {pl.hold === 'NONE'
                  ? 'No hold on the lot or anything it came from'
                  : `Lot ${pl.hold === 'RECALLED' ? 'recalled' : 'on hold'}`}
              </Done>
              <Done done={!fig.surplus}>
                Balance:{' '}
                {fig.surplus
                  ? 'surplus (holds)'
                  : fig.aboveLimit
                    ? 'no surplus · loss noted'
                    : 'no surplus'}
              </Done>
            </div>
            <b className="mt-1 text-[15px]">At {run.data?.locationCode ?? 'the hall'}</b>
            <span className="text-[15px]">
              {pl.pouchesLeft} pouches{cartons !== null ? ` · ${cartons} cartons` : ''}
              {pl.pouchesDispatched ? ` · ${pl.pouchesDispatched} dispatched` : ''}
            </span>
            {pl.bestBefore && (
              <Note>
                Packed {boardDate(pl.packDate)} · best before {boardDate(pl.bestBefore)}
              </Note>
            )}
            <Note>
              Nothing leaves the hall until a named quality person releases the lot (F7.6).
            </Note>
            <div className="flex-1" />
            <PanelActions>
              <LinkButton
                to={`/c/quality/release/${pl.id}`}
                primary={pl.status === 'CLOSED'}
                icon={<SealCheck aria-hidden="true" />}
              >
                Release
              </LinkButton>
              <LinkButton
                to={`/c/quality/lab-results${pl.batchCode ? `?search=${encodeURIComponent(pl.batchCode)}` : ''}`}
                icon={<TestTube aria-hidden="true" />}
              >
                Lab results
              </LinkButton>
            </PanelActions>
          </SidePanel>
        }
      />
    </BoardPage>
  )
}
